import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, CardBody, DatePicker, EmptyState, Select, Icon, showToast } from '@repo/ui';
import type { SelectOption } from '@repo/ui';
import { ExportButton, type ExportFormat } from '../../../components/ExportButton';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils';
import { type DataTableColumn, DataTable } from '@xtnd/web/shared/components';

import { useMatterLedger, useMatters, useExportMatterLedger } from '../../../queries';
import type { MatterLedgerFiltersState } from '../../../types';
import { SummaryCardsGrid } from '../../../components';

export function MatterLedgerDashboardView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingUrlRef = useRef(false);

  // Export mutation
  const { mutateAsync: exportLedger, isPending: isExporting } = useExportMatterLedger();

  const handleExport = async (format: ExportFormat) => {
    if (!matterId.trim() || !dateRange.startDate || !dateRange.endDate) {
      showToast.error('Please select Matter and Date Range before exporting');
      return;
    }

    try {
      const exportType = format === 'excel' ? 'xls' : format;
      await exportLedger({
        matterId,
        type: exportType as 'csv' | 'xls' | 'pdf',
        startDate: dateRange.startDate.toISOString().split('T')[0],
        endDate: dateRange.endDate.toISOString().split('T')[0],
      });
      showToast.success(`Export as ${format.toUpperCase()} started successfully`);
    } catch (error) {
      showToast.error(
        `Failed to export: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Default to current month (first day to last day of current month)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate, endDate };
  };

  const defaultDateRange = getCurrentMonthRange();

  // Initialize from URL params or defaults
  const [matterId, setMatterId] = useState(() => {
    return searchParams.get('matterId') || '';
  });

  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(
    () => {
      const startDateParam = searchParams.get('startDate');
      const endDateParam = searchParams.get('endDate');
      return {
        startDate: startDateParam ? new Date(startDateParam) : defaultDateRange.startDate,
        endDate: endDateParam ? new Date(endDateParam) : defaultDateRange.endDate,
      };
    }
  );

  const [filters, setFilters] = useState<MatterLedgerFiltersState>(() => {
    const matterIdParam = searchParams.get('matterId') || '';
    const startDateParam =
      searchParams.get('startDate') || defaultDateRange.startDate.toISOString().split('T')[0];
    const endDateParam =
      searchParams.get('endDate') || defaultDateRange.endDate.toISOString().split('T')[0];
    const pageParam = searchParams.get('page');
    const limitParam = searchParams.get('limit');

    return {
      matterId: matterIdParam,
      startDate: startDateParam,
      endDate: endDateParam,
      page: pageParam ? parseInt(pageParam, 10) : 1,
      limit: limitParam ? parseInt(limitParam, 10) : 1000,
    };
  });

  const [shouldLoad, setShouldLoad] = useState(() => {
    // Auto-load if matterId is present in URL
    return !!searchParams.get('matterId');
  });

  // Fetch matters list
  const { data: mattersData, isLoading: mattersLoading } = useMatters();

  // Convert matters to Select options
  const matterOptions: SelectOption[] = useMemo(() => {
    if (!mattersData) return [];
    return mattersData.map(matter => {
      const matterNumber = matter.displayNumber || matter.number || matter.id;
      return {
        value: matter.id,
        label: `${matterNumber}: ${matter.description || ''}`,
      };
    });
  }, [mattersData]);

  // Get selected matter option
  const selectedMatterOption: SelectOption | null = useMemo(() => {
    if (!matterId.trim()) return null;
    return (
      matterOptions.find(opt => opt.value === matterId) || {
        value: matterId,
        label: matterId,
      }
    );
  }, [matterId, matterOptions]);

  const { data, isLoading, error } = useMatterLedger(filters, {
    enabled: shouldLoad && !!filters.matterId.trim(),
  });

  // Sync URL params when filters change
  const updateUrlParams = useCallback(
    (newFilters: MatterLedgerFiltersState) => {
      isUpdatingUrlRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);

      // Remove existing params
      newSearchParams.delete('matterId');
      newSearchParams.delete('startDate');
      newSearchParams.delete('endDate');
      newSearchParams.delete('page');
      newSearchParams.delete('limit');

      // Add new params
      if (newFilters.matterId) {
        newSearchParams.set('matterId', newFilters.matterId);
      }
      if (newFilters.startDate) {
        newSearchParams.set('startDate', newFilters.startDate);
      }
      if (newFilters.endDate) {
        newSearchParams.set('endDate', newFilters.endDate);
      }
      newSearchParams.set('page', newFilters.page?.toString() || '1');
      newSearchParams.set('limit', newFilters.limit?.toString() || '1000');

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Sync state when URL params change (for browser back/forward)
  useEffect(() => {
    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      return;
    }

    const matterIdParam = searchParams.get('matterId');
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const pageParam = searchParams.get('page');
    const limitParam = searchParams.get('limit');

    if (matterIdParam !== null) {
      setMatterId(matterIdParam);
    }
    if (startDateParam && endDateParam) {
      setDateRange({
        startDate: new Date(startDateParam),
        endDate: new Date(endDateParam),
      });
    }
    if (matterIdParam !== null || startDateParam !== null || endDateParam !== null) {
      setFilters(prev => ({
        ...prev,
        matterId: matterIdParam || prev.matterId,
        startDate: startDateParam || prev.startDate,
        endDate: endDateParam || prev.endDate,
        page: pageParam ? parseInt(pageParam, 10) : prev.page,
        limit: limitParam ? parseInt(limitParam, 10) : prev.limit,
      }));
      if (matterIdParam) {
        setShouldLoad(true);
      }
    }
  }, [searchParams]);

  const handleSearch = () => {
    if (!matterId.trim() || !dateRange.startDate || !dateRange.endDate) {
      return;
    }
    // Convert Date objects to yyyy-MM-dd format for API
    const startDateStr = dateRange.startDate.toISOString().split('T')[0];
    const endDateStr = dateRange.endDate.toISOString().split('T')[0];

    const newFilters = {
      ...filters,
      matterId,
      startDate: startDateStr,
      endDate: endDateStr,
      page: 1, // Reset to page 1 on new search
    };

    setFilters(newFilters);
    setShouldLoad(true);
    updateUrlParams(newFilters);
  };

  const summary = (data as any)?.summary;

  // Prepare summary cards
  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        title: 'Total Transactions',
        value: numberUtil.formatNumber(summary.totalTransactions),
        subtitle: '',
        icon: 'HiClipboardList',
        color: 'text-xtnd-blue dark:text-xtnd-blue-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
      {
        title: 'Total In',
        value: numberUtil.formatCurrency(summary.totalIn || 0),
        subtitle: '',
        icon: 'HiArrowUp',
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
      {
        title: 'Total Out',
        value: numberUtil.formatCurrency(summary.totalOut || 0),
        subtitle: '',
        icon: 'HiArrowDown',
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
    ];
  }, [summary]);

  // Define columns for DataTable
  const columns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        align: 'left',
        headerClassName: 'bg-gray-50 dark:bg-gray-700',
        renderCell: (transaction: any) => (
          <span className="text-xtnd-dark dark:text-xtnd-light">
            {transaction.transactionDate
              ? new Date(transaction.transactionDate).toLocaleDateString()
              : '-'}
          </span>
        ),
      },
      {
        key: 'source',
        header: 'Source/Destination',
        align: 'left',
        headerClassName: 'bg-gray-50 dark:bg-gray-700',
        renderCell: (transaction: any) => {
          const source = transaction.source || '-';
          if (source === '-') {
            return <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">-</span>;
          }

          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-xtnd-dark dark:bg-gray-700 dark:text-gray-200">
              {source}
            </span>
          );
        },
      },
      {
        key: 'account',
        header: 'Account',
        align: 'left',
        headerClassName: 'bg-gray-50 dark:bg-gray-700',
        renderCell: (transaction: any) => {
          if (!transaction.accountName)
            return <span className="text-xtnd-dark dark:text-xtnd-light">-</span>;

          const accountUrl = transaction.accountId
            ? `https://app.clio.com/nc/#/bank_accounts/${transaction.accountId}/transactions`
            : null;

          if (accountUrl) {
            return (
              <a
                href={accountUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline truncate max-w-xs inline-flex items-center gap-1"
                title={transaction.accountName}
              >
                <span className="truncate">{transaction.accountName}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span
              className="text-xtnd-dark dark:text-xtnd-light truncate max-w-xs"
              title={transaction.accountName}
            >
              {transaction.accountName}
            </span>
          );
        },
      },
      {
        key: 'client',
        header: 'Client',
        align: 'left',
        headerClassName: 'bg-gray-50 dark:bg-gray-700',
        renderCell: (transaction: any) => {
          if (!transaction.clientName)
            return <span className="text-xtnd-dark dark:text-xtnd-light">-</span>;

          const clientUrl = transaction.clientId
            ? `https://app.clio.com/nc/#/contacts/${transaction.clientId}`
            : null;

          if (clientUrl) {
            return (
              <a
                href={clientUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline truncate max-w-xs inline-flex items-center gap-1"
                title={transaction.clientName}
              >
                <span className="truncate">{transaction.clientName}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span
              className="text-xtnd-dark dark:text-xtnd-light truncate max-w-xs"
              title={transaction.clientName}
            >
              {transaction.clientName}
            </span>
          );
        },
      },
      {
        key: 'matter',
        header: 'Matter',
        align: 'left',
        headerClassName: 'bg-gray-50 dark:bg-gray-700',
        renderCell: (transaction: any) => {
          if (!transaction.matterNumber)
            return <span className="text-xtnd-dark dark:text-xtnd-light font-medium">-</span>;

          const matterUrl = transaction.matterId
            ? `https://app.clio.com/nc/#/matters/${transaction.matterId}`
            : null;

          if (matterUrl) {
            return (
              <a
                href={matterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline font-medium inline-flex items-center gap-1"
              >
                <span>{transaction.matterNumber}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span className="text-xtnd-dark dark:text-xtnd-light font-medium">
              {transaction.matterNumber}
            </span>
          );
        },
      },
      {
        key: 'fundsIn',
        header: 'Funds In',
        align: 'right',
        headerClassName: 'bg-green-50 dark:bg-green-900/30',
        className: 'text-green-700 dark:text-green-200',
        renderCell: (transaction: any) => (
          <span className="text-green-600 dark:text-green-400">
            {transaction.fundsIn ? numberUtil.formatCurrency(transaction.fundsIn) : '-'}
          </span>
        ),
      },
      {
        key: 'fundsOut',
        header: 'Funds Out',
        align: 'right',
        headerClassName: 'bg-red-50 dark:bg-red-900/30',
        className: 'text-red-700 dark:text-red-200',
        renderCell: (transaction: any) => (
          <span className="text-red-600 dark:text-red-400">
            {transaction.fundsOut ? numberUtil.formatCurrency(transaction.fundsOut) : '-'}
          </span>
        ),
      },
    ],
    []
  );

  const renderHeader = () => (
    <>
      {/* Summary Cards */}
      {!isLoading && data && summary && summaryCards.length > 0 && (
        <div className="mb-6">
          <SummaryCardsGrid
            cards={summaryCards}
            columns={{ mobile: 1, tablet: 2, desktop: 3 }}
            gap="md"
          />
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* Search Card */}
      <Card variant="outlined">
        <CardBody>
          <div className="flex flex-wrap items-start gap-2 md:items-center md:flex-nowrap">
            {/* Matter Filter */}
            <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
                Matter <span className="text-red-500">*</span>:
              </span>
              <div className="flex-1">
                <Select<SelectOption>
                  value={selectedMatterOption}
                  onChange={option => {
                    if (option && 'value' in option) {
                      setMatterId((option as SelectOption).value);
                    } else {
                      setMatterId('');
                    }
                  }}
                  options={matterOptions}
                  placeholder="Search and select Matter..."
                  isLoading={mattersLoading}
                  disabled={mattersLoading}
                  searchable={true}
                  clearable={true}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
                Date Range <span className="text-red-500">*</span>:
              </span>
              <div className="flex-1">
                <DatePicker
                  id="dateRange"
                  mode="range"
                  label=""
                  value={dateRange}
                  onChange={range => {
                    // Only update when both dates are selected
                    if (range.startDate && range.endDate) {
                      setDateRange({
                        startDate: range.startDate,
                        endDate: range.endDate,
                      });
                    } else {
                      setDateRange(range);
                    }
                  }}
                  format={dateUtil.DateFormat.US}
                  required
                />
              </div>
            </div>

            {/* Search Button */}
            <div className="w-full md:basis-[10%] md:w-auto">
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={!matterId.trim() || !dateRange.startDate || !dateRange.endDate}
                loading={isLoading}
                className="w-full"
                leftIcon={<Icon name="HiSearch" size="sm" />}
              >
                Search
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {!shouldLoad ? (
        <EmptyState
          icon="HiClipboardList"
          title="Enter Matter to view Trust Account ledger"
          description="Please enter Matter, then click Search to view the Trust Account ledger"
          variant="outlined"
          padding="lg"
        />
      ) : (
        <DataTable
          columns={columns}
          data={shouldLoad ? (data as any) : null}
          isLoading={isLoading}
          error={error}
          onRetry={() => {
            if (matterId.trim()) {
              handleSearch();
            }
          }}
          renderHeader={renderHeader}
          renderAction={() => (
            <ExportButton
              onExport={handleExport}
              disabled={!matterId.trim() || !dateRange.startDate || !dateRange.endDate || isLoading}
              loading={isExporting}
            />
          )}
          emptyState={{
            icon: 'HiClipboardList',
            title: 'No Trust Account transactions found',
            description: 'There are no Trust Account transactions matching your search criteria.',
          }}
          tableContainerClassName="overflow-y-auto relative"
          tableClassName="min-w-full divide-y divide-gray-200"
          stickyHeader={true}
          tableMaxHeight="calc(100vh-300px)"
          pageSizeOptions={[25, 50, 100, 200]}
          loadingRowCount={10}
          loadingColumnCount={6}
        />
      )}
    </div>
  );
}
